import * as React from 'react';
import { ChevronDown, ListFilter } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface UserTypeOption {
  value: string;
  label: string;
}

interface UserTypeFilterProps {
  options: UserTypeOption[];
  value: string[];
  onApply: (next: string[]) => void;
  className?: string;
}

function summarise(options: UserTypeOption[], value: string[]) {
  if (value.length === 0) return 'None';
  if (value.length === options.length) return 'All types';
  const labels = options.filter((o) => value.includes(o.value)).map((o) => o.label);
  if (labels.length <= 2) return labels.join(', ');
  return `${labels.length} of ${options.length} types`;
}

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));


export function UserTypeFilter({ options, value, onApply, className }: UserTypeFilterProps) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<string[]>(value);


  React.useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const allValues = React.useMemo(() => options.map((o) => o.value), [options]);
  const allSelected = draft.length === allValues.length;
  const isFiltered = value.length !== allValues.length;
  const dirty = !sameSet(draft, value);

  const toggle = (v: string) =>
    setDraft((d) => (d.includes(v) ? d.filter((x) => x !== v) : [...d, v]));

  const apply = () => {
    if (!draft.length) return;
    setOpen(false);
    if (dirty) onApply(draft);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label="Filter by user type"
          className={cn('h-8 gap-2 px-2.5 font-normal', isFiltered && 'border-primary/60', className)}
        >
          <ListFilter className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">User type:</span>
          <span className="max-w-[150px] truncate font-medium">{summarise(options, value)}</span>
          {isFiltered && (
            <span className="rounded-full bg-primary px-1.5 text-[10px] font-semibold leading-4 text-primary-foreground">
              {value.length}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            User type
          </span>
          <div className="flex gap-3 text-xs">
            <button
              type="button"
              className="text-primary hover:underline disabled:opacity-40 disabled:no-underline"
              disabled={allSelected}
              onClick={() => setDraft(allValues)}
            >
              Select all
            </button>
            <button
              type="button"
              className="text-muted-foreground hover:underline disabled:opacity-40 disabled:no-underline"
              disabled={!draft.length}
              onClick={() => setDraft([])}
            >
              Clear
            </button>
          </div>
        </div>

        <ul className="max-h-64 overflow-y-auto py-1" role="listbox" aria-multiselectable="true">
          {options.map((opt) => {
            const checked = draft.includes(opt.value);
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={checked}
                className="group flex items-center gap-2 px-3 py-1.5 hover:bg-muted/60"
              >
                <label className="flex flex-1 cursor-pointer items-center gap-2.5 text-sm">
                  <Checkbox checked={checked} onCheckedChange={() => toggle(opt.value)} />
                  {opt.label}
                </label>
                <button
                  type="button"
                  className="invisible text-xs text-primary hover:underline group-hover:visible focus-visible:visible"
                  onClick={() => setDraft([opt.value])}
                >
                  Only
                </button>
              </li>
            );
          })}
        </ul>

        <div className="flex items-center justify-between border-t px-3 py-2">
          <span className="text-xs text-muted-foreground">
            {draft.length} of {allValues.length} selected
          </span>
          <Button size="sm" className="h-7" disabled={!draft.length || !dirty} onClick={apply}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}